/**
 * Chart theme constants and configuration for analytics charts
 */

// Batemo color palette
export const COLORS = {
  orange: '#ea5b0c',
  orangeLight: '#ff7a2f',
  cyan: '#0d7c99',
  cyanLight: '#10b9b4',
  green: '#10b981',
  purple: '#8b5cf6',
  red: '#ef4444',
  gray: '#94a3b8',
  background: 'rgba(255, 255, 255, 0.03)',
  grid: 'rgba(255, 255, 255, 0.08)',
};

// ECharts theme settings
export const chartTheme = {
  backgroundColor: 'transparent',
  textStyle: { color: '#94a3b8', fontFamily: 'Roboto, sans-serif' },
  title: {
    textStyle: { color: '#fff', fontFamily: 'Big John, sans-serif', fontSize: 14 },
  },
  legend: { textStyle: { color: '#94a3b8' } },
  tooltip: {
    backgroundColor: 'rgba(15, 23, 42, 0.95)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    textStyle: { color: '#fff' },
  },
};

// Chart explanations for info tooltips
export const CHART_INFO = {
  requestsProTag: {
    title: 'Requests per Day',
    description:
      'Total number of API requests per day. A rising trend means more platform usage. Large swings can indicate special events or problems.',
  },
  responseTimeTrend: {
    title: 'Response Time Trend',
    description:
      'Speed of API responses. "Avg" is the average; "P95" means 95% of all requests were faster than this value. Lower is better. Values above 500ms can indicate problems.',
  },
  requestsNachUhrzeit: {
    title: 'Requests by Hour',
    description:
      'When the API is used most (0–23h). Helps understand when most users are active. Peaks show busy times, troughs show quiet times.',
  },
  topEndpoints: {
    title: 'Top Features',
    description:
      'The most-used platform features. Shows which features are used most: Cell Explorer, Compare, Benchmarking, individual cell data, etc.',
  },
  topLaender: {
    title: 'Top Countries',
    description:
      'Where API requests come from. Based on IP geolocation. Helps understand which markets use the platform most.',
  },
  authVsAnonym: {
    title: 'Authenticated vs. Anonymous',
    description:
      'Compares logged-in users (Authenticated) with non-logged-in visitors (Anonymous). A high share of authenticated requests indicates active, paying users.',
  },
  trafficTrend: {
    title: 'Traffic Trend',
    description:
      'Website visitors and page views per day. "Visitors" are unique people, "Page views" counts all page visits. More visitors = more interest in the platform.',
  },
  bounceRateTrend: {
    title: 'Bounce Rate Trend',
    description:
      'Percentage of visitors who leave after a single page. Low (under 40%) = good, high (over 60%) = visitors do not find what they are looking for. Ideal: 30–50%.',
  },
  topSeiten: {
    title: 'Top Pages',
    description:
      'The most-visited pages of the website. Shows which content is most popular. Helps understand what users are most interested in.',
  },
  trafficQuellen: {
    title: 'Traffic Sources',
    description:
      '"Direct" = direct URL entry, "Google" = search engine, others = links from external sites. Shows how visitors find the site.',
  },
  apiRequests: {
    title: 'API Requests',
    description:
      'Total backend API requests per day. Shows the technical usage of the platform. Correlates with user activity.',
  },
  funnelScore: {
    title: 'Funnel Score',
    description:
      'Weighted score (0–100) based on the conversion rates of each funnel stage vs. target values. 70+ = healthy (green), 40–70 = needs attention (orange), under 40 = critical (red).',
  },
  funnelBesucher: {
    title: 'Visitors',
    description:
      'Unique visitors to the Insights landing page in the selected period. Data source: Plausible Analytics (insights.batemo.com).',
  },
  funnelSignups: {
    title: 'Signups',
    description:
      'Number of new registrations (Create Account) in the selected period. Shows how many landing-page visitors created an account.',
  },
  funnelDemos: {
    title: 'Demos',
    description:
      'Number of started demo accesses in the selected period. Users who started a demo after registration and approval.',
  },
  funnelPp: {
    title: 'Percentage Points (pp.)',
    description:
      'pp. = percentage points — the absolute change of a rate vs. the previous period. Example: if a rate rises from 3% to 5%, that is +2 pp. (not +66%).',
  },
  funnelConversionRate: {
    title: 'Conversion Rate',
    description:
      'Percentage of users who move from one funnel stage to the next. Green (>50%) = good, Orange (10-50%) = moderate, Red (<10%) = low. The pp. values show the change vs. previous period.',
  },
  funnelActiveDemos: {
    title: 'Active Demo',
    description:
      'Users whose demo period is still active (trial_end > today). These users are currently testing the platform.',
  },
  funnelPostDemo: {
    title: 'Post Demo',
    description:
      'Users whose demo has expired but who were recently active. Potential conversion candidates.',
  },
  funnelLost: {
    title: 'Lost',
    description:
      'Users whose demo has expired and who have been inactive for over 30 days. Likely lost.',
  },
  funnelAcquisition: {
    title: 'Acquisition Funnel',
    description:
      'Shows the path from batemo.com product pages to the Insights App. "via batemo.com" = visitors who came through CTA buttons on the marketing website. "Total Visitors" = all visitors including direct access and other sources.',
  },
  funnelConversion: {
    title: 'Conversion Funnel',
    description:
      'Shows the path from first visit to paying customer. The "Insights App" stage shows estimated new visitors (total minus login events) to provide a more accurate conversion denominator. "Active Demo" combines email verification, account approval, and demo activation into one stage. Admin-created accounts (trade shows) can be excluded via the toggle.',
  },
  funnelAbsoluteRate: {
    title: 'Absolute Conversion',
    description:
      'Shows the exact numbers behind the conversion rate. E.g. "118 of 17K" means: 118 people reached the next stage, out of 17,000 in the previous stage.',
  },
  trendsLF1ActiveDemo: {
    title: 'New Active Demos & Conversion (LF1)',
    description:
      'Bars: number of NEWLY-won active demos per bucket (the weekly/daily flow, not the cumulative stock) — users whose demo first became active in that period (first DEMO/ACTIVE/APPROVED transition, with activity afterwards). "Active afterwards" = a heartbeat session OR a fresh login after activation (last_login alone misses token-based returns). Line: landing-page → active-demo rate = new active demos ÷ landing-page visitors in the same bucket. Both are flows, so the rate stays ≤100%. NOTE: the shaded region before the dashed line (2026-05-18) predates heartbeat tracking and falls back to last_login only → undercounts there. Sources: subscription history + user_sessions (DB), Plausible for visitors.',
  },
  trendsLF2Sources: {
    title: 'Landing-Page Visitors by Acquisition Channel (LF2)',
    description:
      'Stacked landing-page visitors per bucket split into disjoint acquisition channels: Google, Bing, LinkedIn, Direct, Cell pages (referrals from cell pages, Plausible source "cell-page"), and Other (residual: DuckDuckGo, ChatGPT, trade-show links, etc.). Classified by Plausible visit source for ALL Insights landing-page visitors (/products/batemo-insights/*), regardless of entry page — so Google/Bing match the Plausible source breakdown. The same-session funnel from internal pages is measured separately in Internal Source Efficiency.',
  },
  trendsLF2bInternal: {
    title: 'Internal Source Efficiency (LF2b)',
    description:
      'Same-session conversion from internal pages to the Insights landing page. Explorer → LP = sessions that entered on the Cell Explorer AND reached the landing page ÷ sessions that entered on the Cell Explorer. Cell pages → LP = the equivalent for cell/product pages. Numerator and denominator share one entry-page session basis, so the rate is naturally ≤100%. Source: Plausible per bucket. Note: multi-visit journeys across sessions are not captured.',
  },
  trendsLF3Engagement: {
    title: 'Demo Engagement Time (LF3)',
    description:
      'Average active minutes per demo user per bucket. Numerator: summed active session minutes (heartbeat-based, counted only while the tab is visible). Denominator: distinct demo users with at least one session start in the bucket; users without activity in the period are excluded. Source: user_sessions table (DB).',
  },
  trendsLF4SubStages: {
    title: 'Refined Funnel Sub-Stages (LF4)',
    description:
      'Snapshot of the funnel between Registration and PRO: Demo Requested → Demo Accepted → Active Demo → PRO. Stages are cumulative (each later stage is a subset of the previous one), so the funnel stays monotonically decreasing even when an admin directly approves a user. "Active Demo" = accepted AND active after activation (heartbeat session OR login). NOTE: this card is a single aggregate over the period — for a window reaching before the heartbeat start (2026-05-18) the pre-heartbeat part falls back to last_login (conservative); a caption flags this. Each conversion shows the share advancing from the prior stage. Source: current subscription state + user_sessions (DB).',
  },
  trendsLF5TradeFair: {
    title: 'Trade Fair Demos — booth proxy (LF5)',
    description:
      'Transitional proxy: trade fair = booth accounts the team created manually (signup_source=admin_created, Batemo employees excluded). Cohort view per bucket: of the booth accounts registered in that period, bars show how many became active demos and the line shows the activation rate = active ÷ registered (same cohort, so always ≤100%). Note: admin_created can also include internal test accounts; will be replaced once a dedicated trade-fair flag exists. The shaded region before the dashed line (2026-05-18) predates heartbeat tracking (last_login fallback, undercounts). Source: users + subscription history + user_sessions (DB).',
  },
  trendsLF6Cumulative: {
    title: 'Cumulative Active Demos: Trade Fair vs. Organic (LF6)',
    description:
      'Active-demo count at the end of each bucket, split into Trade Fair (manually created booth accounts, signup_source=admin_created) vs Organic (everything else — self-registered demos). Counts users who were ever an active demo (demo activated + heartbeat session or login after activation) up to the bucket end — monotonically non-decreasing. The shaded region before the dashed line (2026-05-18) predates heartbeat tracking (last_login fallback, undercounts). Source: users + subscription history + user_sessions (DB).',
  },
};
